import axios from '@/lib/axios'
import { useCreditStore } from '@/stores/credit'
import { useMutation, useQuery } from '@tanstack/vue-query'
import { get } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { MaybeRef, ref, watch } from 'vue'
import { useToast } from 'vue-toastification'

import useCustomMutation from '@/composables/useCustomMutation'

import { Credit } from '@/interfaces/credit'
import { CustomAxiosRequestConfig } from '@/interfaces/customAxiosRequestConfig'
import { MutationParams } from '@/interfaces/mutationParams'

interface Props {
	params?: Params
	immediate?: boolean
}

interface Params extends Omit<Partial<Credit>, 'id'> {
	id?: MaybeRef<number | string | undefined>
	companyId: MaybeRef<number | string | undefined>
}

interface CreateParams
	extends Omit<Params, keyof Credit>,
		Omit<
			Credit,
			'id' | 'creditableId' | 'creditableType' | 'creditableId' | 'type'
		> {}

interface UpdateParams extends Params {}
interface DeleteParams {
	id: number | string
}
interface RestoreParams {
	id: number | string
}

const getCompanyCredit = async (params?: Params) => {
	if (params) {
		const { data } = await axios.get(
			`/api/companies/${get(params.companyId)}/credits`,
			{ errorHandling: false } as CustomAxiosRequestConfig
		)
		return data
	}
}

const createCompanyCredit = async (params: CreateParams) => {
	const { data } = await axios.post(
		`/api/companies/${params.companyId}/credits`,
		params
	)

	return data
}

const updateCompanyCredit = async (params: UpdateParams) => {
	const { data } = await axios.put(`/api/credits/${params.id}`, params)
	return data
}

const deleteCompanyCredit = async (params: DeleteParams) => {
	const { data } = await axios.delete(`/api/credits/${params.id}`)
	return data
}

const restoreCompanyCredit = async (params: Pick<UpdateParams, 'id'>) => {
	const { data } = await axios.post(`/api/credits/${params.id}/restore`)
	return data
}

const useCompanyCredit = (props?: Props) => {
	const toast = useToast()
	const { params, immediate = false } = props || {}
	const companyId = ref(get(params?.companyId) || get(props?.params?.companyId))

	const { setCredit } = useCreditStore()
	const { credit } = storeToRefs(useCreditStore())

	const { data, refetch, status, isLoading, error } = useQuery<Credit>(
		['companyCredit', props?.params?.companyId || companyId.value],
		() =>
			getCompanyCredit({
				companyId: props?.params?.companyId || companyId.value,
			}),
		{
			enabled: params ? immediate : false,
			retry: false,
		}
	)

	const createMutation = useMutation(
		({ params }: MutationParams<CreateParams>) => createCompanyCredit(params),
		useCustomMutation(toast, 'create')
	)

	const updateMutation = useMutation(
		({ params }: MutationParams<UpdateParams>) => updateCompanyCredit(params),
		useCustomMutation(toast, 'update')
	)

	const deleteMutation = useMutation(
		({ params }: MutationParams<DeleteParams>) => deleteCompanyCredit(params),
		useCustomMutation(toast, 'delete')
	)

	const restoreMutation = useMutation(
		({ params }: MutationParams<RestoreParams>) => restoreCompanyCredit(params),
		useCustomMutation(toast, 'update')
	)

	watch(
		data,
		() => {
			setCredit(data.value)
		},
		{ immediate: true }
	)

	return {
		companyId,
		credit: data,
		refetch,
		isLoading,
		error,

		statusGetCompanyCredit: status,
		refetchCompanyCredit: refetch,

		createCompanyCreditMutation: createMutation.mutateAsync,
		createCompanyCreditLoading: createMutation.isLoading,

		updateCompanyCreditMutation: updateMutation.mutate,
		updateCompanyCredit: updateMutation.mutate,
		updateCompanyCreditAsync: updateMutation.mutateAsync,
		updateCompanyCreditLoading: updateMutation.isLoading,

		deleteCompanyCreditMutation: deleteMutation.mutate,
		deleteCompanyCredit: deleteMutation.mutate,
		deleteCompanyCreditAsync: deleteMutation.mutateAsync,

		restoreCompanyCreditMutation: restoreMutation.mutate,
		restoreCompanyCredit: restoreMutation.mutate,
		restoreCompanyCreditAsync: restoreMutation.mutateAsync,
	}
}

export default useCompanyCredit
